// Turns a Nova answer's rendered HTML (see novaAnswerParser.renderNovaMarkdown) into
// real downloadable files. Shared by every export format so headings/lists/tables are
// interpreted once and Word/PDF can't drift out of sync on structure.

// A stretch of text, optionally a hyperlink — kept as runs (not flattened to a string)
// so the answer's links, References especially, stay clickable in Word and PDF.
export type NovaExportRun = { text: string; href?: string };

export type NovaExportBlock =
  | { type: 'heading'; level: number; runs: NovaExportRun[] }
  | { type: 'paragraph'; runs: NovaExportRun[] }
  | { type: 'list'; ordered: boolean; items: NovaExportRun[][] }
  | { type: 'table'; rows: NovaExportRun[][][]; hasHeader: boolean }
  | { type: 'image'; src: string; alt?: string };

type LoadedExportImage = { dataUrl: string; width: number; height: number };

const MAX_EXPORT_IMAGE_PX = 1600;

const rasterizeExportImage = (img: HTMLImageElement): LoadedExportImage | null => {
  const scale = Math.min(1, MAX_EXPORT_IMAGE_PX / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  try {
    return { dataUrl: canvas.toDataURL('image/jpeg', 0.9), width, height };
  } catch {
    return null;
  }
};

const decodeImage = (src: string, crossOrigin: boolean): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img.naturalWidth > 0 ? img : null);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const loadExportImage = async (src: string): Promise<LoadedExportImage | null> => {
  try {
    const response = await fetch(src, { mode: 'cors', cache: 'no-store' });
    if (response.ok) {
      const objectUrl = URL.createObjectURL(await response.blob());
      try {
        const img = await decodeImage(objectUrl, false);
        const loaded = img && rasterizeExportImage(img);
        if (loaded) return loaded;
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    }
  } catch {
    // fall through to a direct CORS image load
  }
  const img = await decodeImage(src, true);
  return img ? rasterizeExportImage(img) : null;
};

const loadExportImages = async (blocks: NovaExportBlock[]): Promise<Map<string, LoadedExportImage | null>> => {
  const srcs = Array.from(
    new Set(blocks.flatMap((block) => (block.type === 'image' ? [block.src] : []))),
  );
  const loaded = await Promise.all(srcs.map(loadExportImage));
  return new Map(srcs.map((src, index) => [src, loaded[index]]));
};

const LINK_COLOR_HEX = '0563C1';
const LINK_COLOR_RGB: [number, number, number] = [5, 99, 193];

// The Stylus wordmark (same artwork as public/_nuxt/img/logo_stylus.7da991d.svg),
// inlined so every export format can rasterize it itself rather than depending on
// a network fetch of a static asset at export time.
const STYLUS_LOGO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 178 33">
  <g fill="#000000">
    <path d="M46.9727 5.48454V32.5082H41.1286V5.48454H30.5837V0.48999H57.5158V5.48454H46.9727Z"/>
    <path d="M4.47263 22.6529C6.75624 26.1311 10.1783 28.1374 13.9229 28.1374C17.6203 28.1374 20.4055 26.4874 20.4055 23.4118C20.4055 20.1564 16.9818 19.7093 12.0971 18.551C7.16668 17.396 1.87148 15.9637 1.87148 9.72015C1.87148 3.79005 7.07547 0 13.6949 0C19.3093 0 23.6908 2.58555 25.8832 5.9301L21.5913 9.54195C19.7654 6.7782 17.3467 4.8609 13.5125 4.8609C9.99586 4.8609 7.57713 6.6 7.57713 9.141C7.57713 11.946 10.0871 12.5301 14.5158 13.5118C19.811 14.6668 26.1568 15.8747 26.1568 22.8311C26.1568 29.1192 20.4967 32.9984 13.7405 32.9984C8.12607 32.9984 2.55555 30.3683 0 26.2647L4.47263 22.6529Z"/>
    <path d="M85.186 0.48999H91.3933L79.5699 19.3099V32.5082H73.7274V19.3099L61.8584 0.48999H68.2042L76.6951 14.0909L85.186 0.48999Z"/>
    <path d="M95.5686 0.48999H101.411V27.5137H116.841V32.5082H95.5686V0.48999Z"/>
    <path d="M166.359 13.5118C161.93 12.5218 159.42 11.9509 159.42 9.141C159.42 6.6 161.839 4.8609 165.354 4.8609C169.188 4.8609 171.604 6.7782 173.435 9.54195L177.725 5.9301C175.529 2.58555 171.153 0 165.536 0C158.917 0 153.713 3.795 153.713 9.72015C153.713 15.9637 159.008 17.391 163.939 18.551C168.823 19.706 172.247 20.1564 172.247 23.4118C172.247 26.4874 169.464 28.1374 165.765 28.1374C162.022 28.1374 158.598 26.1311 156.316 22.6529L151.842 26.2647C154.399 30.3683 159.968 32.9984 165.582 32.9984C172.338 32.9984 177.998 29.1192 177.998 22.8311C177.998 15.8747 171.654 14.6701 166.359 13.5118Z"/>
    <path d="M141.437 0.476807V14.1718C141.437 20.0128 138.471 21.9697 134.042 21.9697C129.661 21.9697 126.693 20.0128 126.693 14.1718V0.476807H120.849V14.3368C120.849 14.3847 120.849 14.4292 120.849 14.4771C120.849 14.5249 120.849 14.5463 120.849 14.5826C120.849 14.8796 120.864 15.175 120.884 15.4687C121.114 18.73 122.601 21.7849 125.046 24.0165C127.492 26.2481 130.713 27.4903 134.059 27.4923H134.123C137.461 27.4783 140.669 26.2309 143.104 24.0008C145.539 21.7707 147.02 18.7231 147.249 15.4704C147.267 15.1404 147.279 14.8104 147.281 14.4804C147.281 14.4325 147.281 14.388 147.281 14.3401V0.480105L141.437 0.476807Z"/>
    <path d="M134.226 27.499H134.057C132.045 27.4992 130.059 27.9494 128.252 28.8151C126.445 29.6808 124.865 30.9391 123.633 32.4936H144.504C143.287 30.9582 141.731 29.7115 139.951 28.8466C138.171 27.9817 136.214 27.521 134.226 27.499Z"/>
  </g>
</svg>`;
const STYLUS_LOGO_ASPECT = 33 / 178;

// Rasterized once per session and reused across every export in it — an
// export click needs the logo synchronously-ish, and re-decoding the same SVG
// on every Word/PDF click would just waste time for an identical
// result. Renders at 4x for crispness since the printed/on-slide size is small.
let stylusLogoDataUrlPromise: Promise<string> | null = null;
const getStylusLogoDataUrl = (): Promise<string> => {
  if (!stylusLogoDataUrlPromise) {
    const promise: Promise<string> = new Promise((resolve, reject) => {
      const scale = 4;
      const svgBlob = new Blob([STYLUS_LOGO_SVG], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 178 * scale;
        canvas.height = 33 * scale;
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (event) => {
        URL.revokeObjectURL(url);
        reject(event instanceof Error ? event : new Error('Failed to rasterize Stylus logo'));
      };
      img.src = url;
    });
    // If rasterization fails, forget the cached promise so the next export
    // attempt tries again from scratch instead of every export for the rest
    // of the session silently dropping the logo because of one bad attempt.
    stylusLogoDataUrlPromise = promise.catch((error) => {
      stylusLogoDataUrlPromise = null;
      throw error;
    });
  }
  return stylusLogoDataUrlPromise;
};

// Only real web/mail links stay clickable — anything else (javascript:, relative paths)
// is exported as plain text.
const SAFE_LINK_RE = /^(https?:|mailto:)/i;

const collectRuns = (node: Node, href: string | undefined, out: NovaExportRun[]) => {
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = (child.textContent || '').replace(/\s+/g, ' ');
      if (text) out.push({ text, href });
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return;
    const el = child as Element;
    if (el.tagName.toLowerCase() === 'a') {
      const target = el.getAttribute('href') || '';
      collectRuns(el, SAFE_LINK_RE.test(target) ? target : href, out);
      return;
    }
    collectRuns(el, href, out);
  });
};

// An element's inline content as runs: whitespace collapsed, neighbouring runs with the
// same link merged, and the outer edges trimmed.
const inlineRuns = (el: Element): NovaExportRun[] => {
  const raw: NovaExportRun[] = [];
  collectRuns(el, undefined, raw);

  const merged: NovaExportRun[] = [];
  raw.forEach((run) => {
    const last = merged[merged.length - 1];
    if (last && last.href === run.href) last.text += run.text;
    else merged.push({ ...run });
  });
  if (merged.length) {
    merged[0].text = merged[0].text.replace(/^\s+/, '');
    merged[merged.length - 1].text = merged[merged.length - 1].text.replace(/\s+$/, '');
  }
  return merged.filter((run) => run.text);
};

const hasText = (runs: NovaExportRun[]): boolean => runs.some((run) => run.text.trim());

const runsToText = (runs: NovaExportRun[]): string => runs.map((run) => run.text).join('');

// Walks the answer's top-level HTML nodes into a flat block list. Only the tags
// renderNovaMarkdown/marked actually emit (headings, paragraphs, lists, tables) are
// handled — anything else (e.g. a stray <div>) is skipped rather than guessed at.
export const htmlToExportBlocks = (html: string): NovaExportBlock[] => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const blocks: NovaExportBlock[] = [];

  const pushImages = (el: Element) => {
    const imgs = el.tagName.toLowerCase() === 'img' ? [el] : Array.from(el.querySelectorAll('img'));
    imgs.forEach((img) => {
      const src = img.getAttribute('src') || '';
      if (/^(https?:|data:image\/)/i.test(src)) {
        blocks.push({ type: 'image', src, alt: img.getAttribute('alt') || undefined });
      }
    });
  };

  container.childNodes.forEach((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (tag === 'img') {
      pushImages(el);
      return;
    }

    if (/^h[1-6]$/.test(tag)) {
      const runs = inlineRuns(el);
      if (hasText(runs)) blocks.push({ type: 'heading', level: Number(tag[1]), runs });
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      const items = Array.from(el.querySelectorAll(':scope > li'))
        .map((li) => inlineRuns(li))
        .filter(hasText);
      if (items.length) blocks.push({ type: 'list', ordered: tag === 'ol', items });
      return;
    }

    if (tag === 'table') {
      const trs = Array.from(el.querySelectorAll('tr'));
      const rows = trs
        .map((row) => Array.from(row.querySelectorAll('th, td')).map((cell) => inlineRuns(cell)))
        .filter((row) => row.some(hasText));
      // Only style the first row as a header if the source markup actually
      // marks it as one (<thead>/<th>) — renderNovaMarkdown's tables always
      // do, but a table dropped straight into the answer as raw HTML might
      // not, and shouldn't have a random data row bolded as if it were one.
      const hasHeader = trs.length > 0 && trs[0].querySelector('th') !== null;
      if (rows.length) blocks.push({ type: 'table', rows, hasHeader });
      return;
    }

    if (tag === 'blockquote' || tag === 'p') {
      const runs = inlineRuns(el);
      if (hasText(runs)) blocks.push({ type: 'paragraph', runs });
      pushImages(el);
      return;
    }

    // Anything else (e.g. a wrapping <div>) — recurse one level down so its
    // paragraphs/lists still make it into the export instead of being dropped.
    el.querySelectorAll(':scope > h1, :scope > h2, :scope > h3, :scope > h4, :scope > h5, :scope > h6, :scope > p, :scope > ul, :scope > ol, :scope > table, :scope > img').forEach(
      (child) => {
        const childHtml = htmlToExportBlocks(child.outerHTML);
        blocks.push(...childHtml);
      },
    );
  });

  return blocks;
};

// The plain-text flavour of a copy: the same blocks as the files, with each link's
// URL written out after its text since plain text can't carry a hyperlink.
export const htmlToPlainText = (html: string): string => {
  const withUrls = (runs: NovaExportRun[]) =>
    runs.map((run) => (run.href && run.href !== run.text ? `${run.text} (${run.href})` : run.text)).join('');

  return htmlToExportBlocks(html)
    .map((block) => {
      switch (block.type) {
        case 'heading':
        case 'paragraph':
          return withUrls(block.runs);
        case 'list':
          return block.items.map((item, index) => `${block.ordered ? `${index + 1}.` : '-'} ${withUrls(item)}`).join('\n');
        case 'table':
          return block.rows.map((row) => row.map(withUrls).join('\t')).join('\n');
        case 'image':
          return block.src.startsWith('data:') ? '' : block.src;
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join('\n\n');
};

export const sanitizeExportFileName = (name: string): string => {
  const trimmed = name.replace(/[\\/:*?"<>|]/g, ' ').trim();
  return trimmed || 'Nova report';
};

export const buildNovaDocx = async (title: string, blocks: NovaExportBlock[]): Promise<Blob> => {
  const {
    Document,
    Packer,
    Paragraph,
    HeadingLevel,
    TextRun,
    ExternalHyperlink,
    ImageRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
  } = await import('docx');
  const logo = await getStylusLogoDataUrl().catch(() => null);
  const loadedImages = await loadExportImages(blocks);
  const LOGO_WIDTH = 140;
  // Sizes are in docx pixels (96 per inch); A4's default text width is ~600px.
  // A lone image stays well under half a page, and a run of back-to-back images
  // is tiled two per row so a gallery doesn't spend a whole page per picture.
  const MAX_IMAGE_WIDTH = 420;
  const MAX_IMAGE_HEIGHT = 320;
  const GRID_COLUMNS = 2;
  const GRID_IMAGE_WIDTH = 270;
  const GRID_IMAGE_HEIGHT = 220;
  const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
  const NO_BORDERS = {
    top: NO_BORDER,
    bottom: NO_BORDER,
    left: NO_BORDER,
    right: NO_BORDER,
    insideHorizontal: NO_BORDER,
    insideVertical: NO_BORDER,
  };

  const toImageRun = (image: LoadedExportImage, maxWidth: number, maxHeight: number) => {
    const scale = Math.min(1, maxWidth / image.width, maxHeight / image.height);
    return new ImageRun({
      type: 'jpg',
      data: image.dataUrl,
      transformation: { width: Math.round(image.width * scale), height: Math.round(image.height * scale) },
    });
  };

  const imageFallback = (block: Extract<NovaExportBlock, { type: 'image' }>) =>
    block.src.startsWith('data:')
      ? []
      : [new Paragraph({ children: toDocxRuns([{ text: block.alt || block.src, href: block.src }]), spacing: PARAGRAPH_SPACING })];

  const headingLevels = [
    HeadingLevel.HEADING_1,
    HeadingLevel.HEADING_2,
    HeadingLevel.HEADING_3,
    HeadingLevel.HEADING_4,
    HeadingLevel.HEADING_5,
    HeadingLevel.HEADING_6,
  ];

  // A plain `new Table(...)` renders with no visible gridlines and no header
  // styling in Word (docx's default table style carries no borders at all) —
  // it reads as loose, misaligned text rather than a table. These make it
  // look like one, and match the dark-header look already used for the
  // PDF export so a report looks the same shape in every format.
  const TABLE_BORDER = { style: BorderStyle.SINGLE, size: 4, color: 'D9D9D9' };
  const TABLE_BORDERS = {
    top: TABLE_BORDER,
    bottom: TABLE_BORDER,
    left: TABLE_BORDER,
    right: TABLE_BORDER,
    insideHorizontal: TABLE_BORDER,
    insideVertical: TABLE_BORDER,
  };
  const CELL_MARGINS = { top: 80, bottom: 80, left: 120, right: 120 };
  const HEADER_FILL = '2F2F2F';

  const PARAGRAPH_SPACING = { line: 276, after: 160 };

  // Every ordered list must get its own `instance` — sharing one instance
  // (or omitting it) across separate numbered lists makes docx treat them as
  // one continuous list, so a second list would keep counting from where the
  // first left off instead of restarting at 1.
  let orderedListInstance = 0;

  // Runs → Word runs, with each link as a real hyperlink (blue, underlined unless the
  // surrounding text already has its own colour, as in a table's dark header row).
  const toDocxRuns = (runs: NovaExportRun[], base: { bold?: boolean; color?: string } = {}) =>
    runs.map((run) =>
      run.href
        ? new ExternalHyperlink({
            link: run.href,
            children: [new TextRun({ ...base, text: run.text, color: base.color ?? LINK_COLOR_HEX, underline: {} })],
          })
        : new TextRun({ ...base, text: run.text }),
    );

  const children = blocks.flatMap((block, index) => {
    switch (block.type) {
      case 'heading':
        return [
          new Paragraph({
            heading: headingLevels[Math.min(block.level, 6) - 1],
            children: toDocxRuns(block.runs),
            spacing: { before: 240, after: 120 },
          }),
        ];
      case 'paragraph':
        return [new Paragraph({ children: toDocxRuns(block.runs), spacing: PARAGRAPH_SPACING })];
      case 'list': {
        const instance = block.ordered ? ++orderedListInstance : undefined;
        return block.items.map(
          (item) =>
            new Paragraph({
              children: toDocxRuns(item),
              bullet: block.ordered ? undefined : { level: 0 },
              numbering: block.ordered ? { reference: 'nova-export-numbering', level: 0, instance } : undefined,
              spacing: { ...PARAGRAPH_SPACING, after: 80 },
            }),
        );
      }
      case 'table':
        return [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: TABLE_BORDERS,
            rows: block.rows.map(
              (row, rowIndex) => {
                const isHeader = block.hasHeader && rowIndex === 0;
                return new TableRow({
                  tableHeader: isHeader,
                  children: row.map(
                    (cell) =>
                      new TableCell({
                        margins: CELL_MARGINS,
                        shading: isHeader ? { fill: HEADER_FILL } : undefined,
                        children: [
                          new Paragraph({
                            children: toDocxRuns(cell, { bold: isHeader, color: isHeader ? 'FFFFFF' : undefined }),
                          }),
                        ],
                      }),
                  ),
                });
              },
            ),
          }),
          new Paragraph({ text: '', spacing: { after: 120 } }),
        ];
      case 'image': {
        // The first image of a back-to-back run renders the whole run; the rest are skipped.
        if (blocks[index - 1]?.type === 'image') return [];
        const run: Extract<NovaExportBlock, { type: 'image' }>[] = [];
        for (let i = index; blocks[i]?.type === 'image'; i++) {
          run.push(blocks[i] as Extract<NovaExportBlock, { type: 'image' }>);
        }
        const loaded = run.flatMap((item) => {
          const image = loadedImages.get(item.src);
          return image ? [image] : [];
        });
        const fallbacks = run.filter((item) => !loadedImages.get(item.src)).flatMap(imageFallback);

        if (loaded.length === 1) {
          return [
            new Paragraph({ children: [toImageRun(loaded[0], MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT)], spacing: PARAGRAPH_SPACING }),
            ...fallbacks,
          ];
        }
        if (loaded.length === 0) return fallbacks;

        const rows: LoadedExportImage[][] = [];
        for (let i = 0; i < loaded.length; i += GRID_COLUMNS) rows.push(loaded.slice(i, i + GRID_COLUMNS));
        return [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: NO_BORDERS,
            rows: rows.map(
              (row) =>
                new TableRow({
                  cantSplit: true,
                  children: Array.from({ length: GRID_COLUMNS }, (_, column) => {
                    const image = row[column];
                    return new TableCell({
                      width: { size: 100 / GRID_COLUMNS, type: WidthType.PERCENTAGE },
                      borders: NO_BORDERS,
                      margins: CELL_MARGINS,
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: image ? [toImageRun(image, GRID_IMAGE_WIDTH, GRID_IMAGE_HEIGHT)] : [],
                        }),
                      ],
                    });
                  }),
                }),
            ),
          }),
          new Paragraph({ text: '', spacing: { after: 120 } }),
          ...fallbacks,
        ];
      }
      default:
        return [];
    }
  });

  const doc = new Document({
    numbering: {
      config: [
        {
          reference: 'nova-export-numbering',
          levels: [{ level: 0, format: 'decimal', text: '%1.', alignment: 'start' }],
        },
      ],
    },
    sections: [
      {
        children: [
          ...(logo
            ? [
                new Paragraph({
                  children: [
                    new ImageRun({
                      type: 'png',
                      data: logo,
                      transformation: { width: LOGO_WIDTH, height: Math.round(LOGO_WIDTH * STYLUS_LOGO_ASPECT) },
                    }),
                  ],
                  spacing: { after: 320 },
                }),
              ]
            : []),
          new Paragraph({ text: title, heading: HeadingLevel.TITLE }),
          ...children,
        ],
      },
    ],
  });

  return Packer.toBlob(doc);
};

export const buildNovaPdf = async (title: string, blocks: NovaExportBlock[]): Promise<Blob> => {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (lineHeight: number) => {
    if (y + lineHeight > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeLines = (text: string, fontSize: number, bold: boolean, indent = 0, gapAfter = 10) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth - indent);
    const lineHeight = fontSize * 1.35;
    lines.forEach((line: string) => {
      ensureSpace(lineHeight);
      doc.text(line, margin + indent, y);
      y += lineHeight;
    });
    y += gapAfter;
  };

  // Lays runs out word by word (jsPDF has no rich-text layout of its own) so each link
  // can be drawn blue and underlined with a clickable area over exactly its words.
  const writeRuns = (runs: NovaExportRun[], fontSize: number, bold: boolean, indent = 0, gapAfter = 10) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    const lineHeight = fontSize * 1.35;
    const lineWidth = maxWidth - indent;
    let x = 0;

    const nextLine = () => {
      y += lineHeight;
      x = 0;
      ensureSpace(lineHeight);
    };
    const draw = (text: string, width: number, href?: string) => {
      const left = margin + indent + x;
      if (href) {
        doc.setTextColor(...LINK_COLOR_RGB);
        doc.textWithLink(text, left, y, { url: href });
        doc.setDrawColor(...LINK_COLOR_RGB);
        doc.setLineWidth(0.5);
        doc.line(left, y + 1.5, left + width, y + 1.5);
        doc.setTextColor(0, 0, 0);
      } else {
        doc.text(text, left, y);
      }
      x += width;
    };

    ensureSpace(lineHeight);
    runs.forEach((run) => {
      (run.text.match(/\s+|\S+/g) ?? []).forEach((token) => {
        const isSpace = /^\s/.test(token);
        if (isSpace) {
          // Leading spaces on a line are dropped; others only advance the cursor.
          if (x > 0) x += doc.getTextWidth(token);
          return;
        }
        let piece = token;
        while (piece) {
          const width = doc.getTextWidth(piece);
          if (x > 0 && x + width > lineWidth) nextLine();
          if (width > lineWidth - x) {
            // A single word wider than a whole line (a long URL): break it by character.
            let count = piece.length;
            while (count > 1 && doc.getTextWidth(piece.slice(0, count)) > lineWidth - x) count--;
            const head = piece.slice(0, count);
            draw(head, doc.getTextWidth(head), run.href);
            piece = piece.slice(count);
            if (piece) nextLine();
          } else {
            draw(piece, width, run.href);
            piece = '';
          }
        }
      });
    });
    y += lineHeight + gapAfter;
  };

  const loadedImages = await loadExportImages(blocks);
  const logo = await getStylusLogoDataUrl().catch(() => null);
  if (logo) {
    const logoWidth = 100;
    const logoHeight = logoWidth * STYLUS_LOGO_ASPECT;
    doc.addImage(logo, 'PNG', margin, y, logoWidth, logoHeight);
    y += logoHeight + 28;
  }

  writeLines(title, 20, true, 0, 18);

  // Same idea as the Word export: a lone image stays well under half a page, and a run
  // of back-to-back images is tiled two per row instead of one image per page.
  const MAX_IMAGE_WIDTH = 320;
  const MAX_IMAGE_HEIGHT = 240;
  const GRID_COLUMNS = 2;
  const GRID_GAP = 12;
  const GRID_CELL_WIDTH = (maxWidth - GRID_GAP * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
  const GRID_IMAGE_HEIGHT = 180;

  const fitImage = (image: LoadedExportImage, boxWidth: number, boxHeight: number) => {
    const scale = Math.min(1, boxWidth / image.width, boxHeight / image.height);
    return { width: image.width * scale, height: image.height * scale };
  };

  blocks.forEach((block, index) => {
    switch (block.type) {
      case 'heading':
        writeRuns(block.runs, Math.max(16 - block.level, 11), true, 0, 8);
        break;
      case 'paragraph':
        writeRuns(block.runs, 11, false);
        break;
      case 'list':
        block.items.forEach((item, index) =>
          writeRuns([{ text: `${block.ordered ? `${index + 1}.` : '•'} ` }, ...item], 11, false, 14, 4),
        );
        y += 6;
        break;
      case 'table': {
        // jsPDF has no built-in table renderer — writing cells as a
        // pipe-joined line of text (the previous approach) just reads as a
        // wrapped paragraph, not a table. jspdf-autotable draws a real grid,
        // and handles the table itself spanning multiple pages. Only style a
        // head row when the source table actually had one — otherwise a data
        // row would get bolded and shaded as if it were a header.
        // A cell's text is plain strings here, so a link inside one is carried separately
        // (first link per cell) and drawn as blue text with a clickable area over the cell.
        const cellText = block.rows.map((row) => row.map(runsToText));
        const cellHref = block.rows.map((row) => row.map((cell) => cell.find((run) => run.href)?.href));
        const hrefFor = (section: string, rowIndex: number, columnIndex: number) =>
          cellHref[section === 'head' ? 0 : rowIndex + (block.hasHeader ? 1 : 0)]?.[columnIndex];

        autoTable(doc, {
          head: block.hasHeader ? [cellText[0]] : undefined,
          body: block.hasHeader ? cellText.slice(1) : cellText,
          didParseCell: (data) => {
            if (data.section === 'body' && hrefFor(data.section, data.row.index, data.column.index)) {
              data.cell.styles.textColor = LINK_COLOR_RGB;
            }
          },
          didDrawCell: (data) => {
            const url = hrefFor(data.section, data.row.index, data.column.index);
            if (url && (data.section === 'body' || data.section === 'head')) {
              doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
            }
          },
          startY: y,
          margin: { left: margin, right: margin, bottom: margin },
          styles: { fontSize: 9.5, cellPadding: 6, overflow: 'linebreak', valign: 'top' },
          headStyles: { fillColor: [40, 40, 40], textColor: 255, fontStyle: 'bold' },
          theme: 'grid',
        });
        y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 16;
        break;
      }
      case 'image': {
        // The first image of a back-to-back run draws the whole run; the rest are skipped.
        if (blocks[index - 1]?.type === 'image') break;
        const run: Extract<NovaExportBlock, { type: 'image' }>[] = [];
        for (let i = index; blocks[i]?.type === 'image'; i++) {
          run.push(blocks[i] as Extract<NovaExportBlock, { type: 'image' }>);
        }
        const loaded = run.flatMap((item) => {
          const image = loadedImages.get(item.src);
          return image ? [image] : [];
        });

        if (loaded.length === 1) {
          const { width, height } = fitImage(loaded[0], MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT);
          ensureSpace(height);
          doc.addImage(loaded[0].dataUrl, 'JPEG', margin, y, width, height);
          y += height + 14;
        } else if (loaded.length > 1) {
          for (let i = 0; i < loaded.length; i += GRID_COLUMNS) {
            const row = loaded.slice(i, i + GRID_COLUMNS).map((image) => ({
              image,
              ...fitImage(image, GRID_CELL_WIDTH, GRID_IMAGE_HEIGHT),
            }));
            const rowHeight = Math.max(...row.map((cell) => cell.height));
            ensureSpace(rowHeight);
            row.forEach((cell, column) => {
              const cellLeft = margin + column * (GRID_CELL_WIDTH + GRID_GAP);
              doc.addImage(cell.image.dataUrl, 'JPEG', cellLeft + (GRID_CELL_WIDTH - cell.width) / 2, y, cell.width, cell.height);
            });
            y += rowHeight + GRID_GAP;
          }
          y += 2;
        }

        run
          .filter((item) => !loadedImages.get(item.src) && !item.src.startsWith('data:'))
          .forEach((item) => writeRuns([{ text: item.alt || item.src, href: item.src }], 11, false));
        break;
      }
      default:
        break;
    }
  });

  return doc.output('blob');
};
